import {
  AudioRecordingError,
  AudioRecordingErrorSource,
  AudioRecordingErrorStage,
  BaseError,
} from "@/domains/errors";
import { audioRecordingModule } from "@/modules/audio-recording";
import { WebMediaRecorder } from "@/store/audio/recording/media-recorder";
import { WebMediaTranscriber } from "@/store/audio/recording/media-transcriber/web-media-transcriber";
import {
  MediaController,
  MediaControllerEventHandlers,
  MediaRecorderAudioChunk,
  MediaRecorderEngine,
  MediaRecorderTranscriptFinalTurn,
  MediaRecorderTranscriptPartialTurn,
  MediaRecorderWaveformSamples,
  MediaTranscriberEngine,
  MediaControllerInitializeOptions,
  TranscriptionStatus,
} from "@/store/audio/recording/media-types";
import type { AudioLevelProbe } from "@/modules/audio-recording/audio-level-probe";
import { WebMediaVisualizer } from "@/store/audio/recording/media-visualizer";
import { logger } from "@/modules/logger";

export type WebMediaControllerOptions = {
  eventHandlers: MediaControllerEventHandlers;
  customVocabulary?: string[];
};

const WEB_MEDIA_CONTROLLER_KIND = "web_media_controller" as const;

export class WebMediaController implements MediaController {
  readonly kind = WEB_MEDIA_CONTROLLER_KIND;
  private isInitialized: boolean = false;
  private stream: MediaStream | null = null;
  private recorder: MediaRecorderEngine | null = null;
  private transcriber: MediaTranscriberEngine | null = null;
  private readonly handlers: MediaControllerEventHandlers;
  private readonly visualizerEngine = new WebMediaVisualizer();
  private readonly customVocabulary: string[];

  private visualizer: AudioLevelProbe | null = null;
  private recorderNotifiedStop = false;
  private isVoiceDisconnected = false;
  private boundHandleTrackEnded: (() => void) | null = null;

  constructor({ eventHandlers, customVocabulary = [] }: WebMediaControllerOptions) {
    this.handlers = eventHandlers;
    this.customVocabulary = customVocabulary;
  }

  async initializeAndStart(options: MediaControllerInitializeOptions): Promise<void> {
    await this.initialize(options);
    options.onBeginRecording?.();
    await this.start();
  }

  async initialize(_options: MediaControllerInitializeOptions): Promise<void> {
    if (this.isInitialized) return;

    if (!navigator.mediaDevices?.getUserMedia) {
      throw new BaseError({
        message: "Media capture is not supported in this environment.",
        info: {
          error: "MediaDevices API is unavailable.",
        },
      });
    }

    const sampleRate = 16_000;
    let stream: MediaStream | null = null;
    let visualizer: AudioLevelProbe | null = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      } as MediaStreamConstraints);

      const { visualizer: visualizerProbe } = await this.visualizerEngine.start({
        stream,
        sampleRate,
      });
      visualizer = visualizerProbe ?? null;
    } catch (error) {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      try {
        await this.visualizerEngine.stop();
      } catch {
        // ignore cleanup failures
      }
      throw new BaseError({
        message: "Media capture is not supported in this environment.",
        info: {
          error: error instanceof Error ? error.message : String(error),
        },
      });
    }

    const activeStream = stream!;

    // Set up voice disconnect detection on audio tracks
    this.setupVoiceDisconnectDetection(activeStream);

    const recorder = new WebMediaRecorder({ stream: activeStream });
    const transcriber = new WebMediaTranscriber({
      tokenProvider: audioRecordingModule.assemblyAiTokenProvider,
      stream: activeStream,
      customVocabulary: this.customVocabulary,
    });

    recorder.setEventHandlers({
      onAudioChunk: ({ chunk }: { chunk: MediaRecorderAudioChunk }) => {
        this.handlers.onAudioChunk?.({ chunk });
      },
      onWaveformSamples: ({ samples }: { samples: MediaRecorderWaveformSamples }) => {
        this.handlers.onWaveformSamples?.({ samples });
      },
      onError: ({ error }: { error: unknown }) =>
        this.handleError({
          error,
          source: AudioRecordingErrorSource.WebMediaRecorder,
          stage: AudioRecordingErrorStage.Event,
        }),
      onStop: () => {
        this.recorderNotifiedStop = true;
        this.handlers.onStop?.();
      },
    });

    transcriber.setEventHandlers({
      onTranscriptPartial: ({
        partialTurn,
      }: {
        partialTurn: MediaRecorderTranscriptPartialTurn;
      }) => {
        this.handlers.onTranscriptPartial?.({ partialTurn });
      },
      onTranscriptFinal: ({ finalTurn }: { finalTurn: MediaRecorderTranscriptFinalTurn }) => {
        this.handlers.onTranscriptFinal?.({ finalTurn });
      },
      onError: ({ error }: { error: unknown }) => this.handleError({ error }),
      onStatusChange: ({ status }) =>
        this.handlers.onTranscriptionStatusChange?.({ status: status as TranscriptionStatus }),
    });

    this.stream = activeStream;
    this.recorder = recorder;
    this.transcriber = transcriber;
    this.visualizer = visualizer ?? null;
    this.isInitialized = true;
  }

  async start(): Promise<void> {
    (async () => {
      try {
        await this.transcriber?.start();
      } catch (error) {
        if (this.isTranscriptionUnsupportedError(error)) {
          // `handleError` already notified listeners and disposed the transcriber.
        } else {
          // If this recording is to start recording immediately, we cannot wait for the transcription to start.
          logger.error({
            message: "[WebMediaController] start transcription failed",
            info: { error: error instanceof Error ? error.message : String(error) },
          });
        }
        // Swallowing transcription failures keeps recording active; the recorder will
        // continue streaming audio while the UI exposes a “microphone only” message.
      }
    })();

    await this.recorder?.start();
    this.handlers.onStart?.();
  }

  async stop(): Promise<void> {
    await this.recorder?.stop();
    await this.transcriber?.stop();
    try {
      await this.visualizerEngine.stop();
    } catch {
      // ignore cleanup failures
    }
    this.cleanupVoiceDisconnectDetection();
    this.stopStream();
    this.visualizer = null;

    if (!this.recorderNotifiedStop) {
      this.handlers.onStop?.();
    }
    this.recorderNotifiedStop = false;
    this.isInitialized = false;
  }

  async pause(): Promise<void> {
    await this.transcriber?.pause();
    await this.recorder?.pause();
  }

  async resume(): Promise<void> {
    await this.transcriber?.resume();
    await this.recorder?.resume();
  }

  getVisualizer(): AudioLevelProbe | null {
    return this.visualizer;
  }

  dispose(): void {
    this.recorder?.dispose();
    this.transcriber?.dispose();
    this.cleanupVoiceDisconnectDetection();
    this.stopStream();
    this.recorder = null;
    this.transcriber = null;
    this.visualizer = null;
    this.isInitialized = false;
    this.recorderNotifiedStop = false;
    void this.visualizerEngine.stop();
  }

  private stopStream(): void {
    if (!this.stream) return;
    this.stream.getTracks().forEach(track => track.stop());
    this.stream = null;
  }

  private handleError({
    error,
    source,
    stage,
  }: {
    error: unknown;
    source?: AudioRecordingErrorSource;
    stage?: AudioRecordingErrorStage;
  }) {
    const isRecorderError = source === AudioRecordingErrorSource.WebMediaRecorder;
    if (!isRecorderError) {
      if (this.isTranscriptionUnsupportedError(error)) {
        this.handlers.onTranscriptionUnsupported?.();
        this.transcriber?.dispose?.();
        this.transcriber = null;
        return;
      }

      if (this.isTranscriptionUnavailableError(error)) {
        this.handlers.onTranscriptionUnavailable?.();
        return;
      }
    }

    const wrapped = source
      ? error instanceof AudioRecordingError
        ? error
        : new AudioRecordingError({
            source,
            stage,
            message: error instanceof Error ? error.message : String(error),
            originalError: error instanceof Error ? error : undefined,
          })
      : error;
    this.handlers.onError?.({ error: wrapped });
  }

  private isTranscriptionUnsupportedError(error: unknown): boolean {
    const underlying = this.resolveUnderlyingError(error);
    if (underlying instanceof DOMException && underlying.name === "NotSupportedError") {
      return true;
    }

    return false;
  }

  private isTranscriptionUnavailableError(error: unknown): boolean {
    const underlying = this.resolveUnderlyingError(error);
    if (underlying instanceof Error && /transcription/i.test(underlying.message ?? "")) {
      return true;
    }

    return false;
  }

  private resolveUnderlyingError(error: unknown): unknown {
    if (error instanceof BaseError && error.originalError) {
      return error.originalError;
    }
    return error;
  }

  private setupVoiceDisconnectDetection(stream: MediaStream): void {
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) return;

    this.boundHandleTrackEnded = () => {
      if (!this.isVoiceDisconnected) {
        this.isVoiceDisconnected = true;
        logger.warn({
          message: "[WebMediaController] Audio track ended - voice disconnected",
        });
        this.handlers.onVoiceDisconnected?.();
      }
    };

    // Listen for track ended events (fires when device is unplugged)
    for (const track of audioTracks) {
      track.addEventListener("ended", this.boundHandleTrackEnded);
    }
  }

  private cleanupVoiceDisconnectDetection(): void {
    if (!this.stream || !this.boundHandleTrackEnded) return;

    const audioTracks = this.stream.getAudioTracks();
    for (const track of audioTracks) {
      track.removeEventListener("ended", this.boundHandleTrackEnded);
    }
    this.boundHandleTrackEnded = null;
    this.isVoiceDisconnected = false;
  }
}
